import { observable } from "@legendapp/state";
import { getChannelBreakdownReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface ChannelBreakdownState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: ChannelBreakdownState = {
  rows: [],
  isLoading: true,
  error: null,
  loadedForScopeKey: null,
};

export const ChannelBreakdownStore$ = observable(defaultValues);

const guard = createRequestGuard();

export const fetchChannelBreakdownReport = async (scope: ReportScope) => {
  const scopeKey = reportScopeKey(scope);
  const result = guard.check(
    scopeKey,
    ChannelBreakdownStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    ChannelBreakdownStore$.assign({ error: null, isLoading: false });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  ChannelBreakdownStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getChannelBreakdownReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ChannelBreakdownStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ChannelBreakdownStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      ChannelBreakdownStore$.isLoading.set(false);
    }
  }
};

export const clearChannelBreakdownReport = () => {
  guard.invalidate();
  ChannelBreakdownStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
